/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router';

import { StepsIndicator } from '../../components/StepsIndicator/StepsIndicator';
import { fetchSetupProgress } from '../../utils/queryFns';
import { EmailConfigurationStep } from './components/EmailConfigurationStep/EmailConfigurationStep';
import { GetStarted } from './components/GetStarted/GetStarted';
import { OrganizationStep } from './components/OrganizationStep/OrganizationStep';
import { SuperuserStep } from './components/SuperuserStep/SuperuserStep';
import { useSetupPageStyles } from './SetupPage.styles';

export const SetupPage = memo(() => {
  const classes = useSetupPageStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const setupProgressQuery = useQuery([fetchSetupProgress.key], fetchSetupProgress.fn);
  const setupProgress = useMemo(() => setupProgressQuery.data?.data.payload, [setupProgressQuery.data]);

  useEffect(() => {
    if (setupProgress) {
      const isSetupCompleted =
        setupProgress.steps.superuser === 'completed' &&
        setupProgress.steps.organization === 'completed' &&
        setupProgress.steps.emailConfig === 'completed';
      if (isSetupCompleted) {
        void navigate(-1);
      }
      if (setupProgress.steps.superuser === 'completed') {
        setCurrentStep(2);
      }
      if (setupProgress.steps.organization === 'completed') {
        setCurrentStep(3);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupProgress]);

  const goToNextStep = () => {
    setCurrentStep(step => ++step);
  };

  const onComplete = () => {
    void navigate('/');
  };

  return (
    <>
      {currentStep === 0 && <GetStarted goToNextStep={goToNextStep} />}
      {currentStep === 1 && <SuperuserStep onUserCreate={goToNextStep} />}
      {currentStep === 2 && <OrganizationStep onOrganizationCreate={goToNextStep} />}
      {currentStep === 3 && <EmailConfigurationStep onEmailConfigurationCreate={onComplete} />}
      <div
        className={cx(classes.steps, {
          'd-none': currentStep === 0,
        })}
      >
        <StepsIndicator stepNumber={currentStep} stepsCount={3} labelClassName={classes.stepsLabel} />
      </div>
    </>
  );
});
